import { ApiVersions } from '@gonfalon/openapi/api-versions';
import { useQueries } from '@tanstack/react-query';

import type { AggregationType, DimensionType, Granularity } from '../../types';
import { UsageTab } from '../../types';
import { usageTabConfig } from '../constants/usageTabConfig';
import {
  DataExportScope,
  type DataExportSource,
  type DataExportSourceState,
  type DataExportSourceStates,
} from '../utils/combineDataExportUsage';
import {
  isUnsupportedDestinationGroupBy,
  mergeUsageSeriesLists,
  shouldRetryGroupedUsage,
  splitDailyRange,
} from '../utils/dataExportRequests';
import { resolveQueryFn } from '../utils/resolveQueryFn';

type UseDataExportSourceUsageProps = {
  from: number;
  to: number;
  aggregationType: AggregationType;
  granularity: Granularity;
  enabled?: boolean;
  groupBy?: Partial<Record<DataExportSource, DimensionType[]>>;
};

const IDLE = { data: undefined, isLoading: false, isError: false };
const SOURCE_TABS = [
  [DataExportScope.STREAMING, UsageTab.DATA_EXPORT],
  [DataExportScope.WAREHOUSE, UsageTab.WAREHOUSE_DATA_EXPORT],
] as const;

export function useDataExportSourceUsage({
  from,
  to,
  aggregationType,
  granularity,
  enabled = true,
  groupBy,
}: UseDataExportSourceUsageProps): DataExportSourceStates {
  const ranges = splitDailyRange(from, to, granularity);
  const results = useQueries({
    queries: enabled
      ? SOURCE_TABS.flatMap(([source, tab]) =>
          ranges.map((range) => ({
            ...resolveQueryFn(usageTabConfig[tab].queryFnName)({
              apiVersion: ApiVersions.Beta,
              query: {
                aggregationType,
                from: range.from.toString(),
                to: range.to.toString(),
                granularity,
                ...(groupBy ? { groupBy: groupBy[source] } : {}),
              },
            }),
            enabled: !groupBy || !!groupBy[source],
            ...(groupBy ? { retry: shouldRetryGroupedUsage } : {}),
          })),
        )
      : [],
  });

  const toState = (sourceIndex: number): DataExportSourceState => {
    const chunks = results.slice(sourceIndex * ranges.length, (sourceIndex + 1) * ranges.length);
    if (chunks.length === 0) {
      return IDLE;
    }
    const isError = chunks.some((chunk) => chunk.isError);
    const data = chunks.every((chunk) => chunk.data) ? chunks.map((chunk) => chunk.data) : undefined;
    return {
      data:
        data &&
        (data.length === 1 ? data[0] : mergeUsageSeriesLists(data as Array<NonNullable<(typeof data)[number]>>)),
      isLoading: chunks.some((chunk) => chunk.isPending),
      isError,
      isUnsupportedGroupBy: chunks.some((chunk) => chunk.isError && isUnsupportedDestinationGroupBy(chunk.error)),
    };
  };

  return {
    [DataExportScope.STREAMING]: toState(0),
    [DataExportScope.WAREHOUSE]: toState(1),
  };
}
